// TradeMate Pro Simplified Permission System
// Works with JSON permissions structure in users.permissions column

import {
  HomeIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  FolderIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ClockIcon,
  BanknotesIcon,
  ChartBarIcon,
  CogIcon,
  ChatBubbleLeftRightIcon,
  BuildingOfficeIcon,
  WrenchScrewdriverIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  DocumentCheckIcon,
  ArchiveBoxIcon,
  TagIcon,
  ClipboardDocumentListIcon,
  // Coming Soon Feature Icons
  DevicePhoneMobileIcon,
  MapPinIcon,
  EnvelopeIcon,
  // Marketplace Icon
  ShoppingBagIcon,
  CpuChipIcon,
  ComputerDesktopIcon,
  PresentationChartLineIcon,
  CreditCardIcon,
  // Team Chat Icon
  LockClosedIcon,
  BookOpenIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { ROLES } from './roleUtils';

export const MODULES = {
  DASHBOARD: 'dashboard',
  CALENDAR: 'calendar',
  QUOTES: 'quotes',
  JOBS: 'jobs',
  INVOICES: 'invoices',
  CUSTOMERS: 'customers',
  DOCUMENTS: 'documents',
  SETTINGS: 'settings',
  EMPLOYEES: 'employees',
  TIMESHEETS: 'timesheets',
  LABOR: 'labor',
  REPORTS: 'reports',
  TOOLS: 'tools',
  EXPENSES: 'expenses',
  PURCHASE_ORDERS: 'purchase_orders',
  VENDORS: 'vendors',
  INVENTORY: 'inventory',
  RATE_CARDS: 'rate_cards',
  // Board #1245: the customer inbox. Team Chat is deliberately NOT here — it is internal and
  // ungated for every active employee, per the 2026-09-20 operator decision.
  CUSTOMER_MESSAGING: 'customer_messaging',
  // Service Request System
  INCOMING_REQUESTS: 'incoming_requests',
  LEADS: 'leads',
  MARKETPLACE: 'marketplace',
  // Coming Soon Features
  MOBILE_APP: 'mobile_app',
  GPS_TRACKING: 'gps_tracking',
  MARKETING_AUTOMATION: 'marketing_automation',
  AI_ESTIMATING: 'ai_estimating',
  ENHANCED_CUSTOMER_PORTAL: 'enhanced_customer_portal',
  BUSINESS_INTELLIGENCE: 'business_intelligence',
  PAYMENT_PROCESSING: 'payment_processing',
  ADVANCED_SCHEDULING: 'advanced_scheduling'
};

export const MODULE_LABELS = {
  [MODULES.DASHBOARD]: 'Dashboard',
  [MODULES.CALENDAR]: 'Calendar',
  [MODULES.QUOTES]: 'Quotes',
  [MODULES.JOBS]: 'Jobs',
  [MODULES.INVOICES]: 'Invoices',
  [MODULES.CUSTOMERS]: 'Customers',
  [MODULES.DOCUMENTS]: 'Company Documents',
  [MODULES.SETTINGS]: 'Settings',
  [MODULES.EMPLOYEES]: 'Employees',
  [MODULES.TIMESHEETS]: 'Time Tracking',
  [MODULES.LABOR]: 'Labor Costs',
  [MODULES.REPORTS]: 'Reports',
  [MODULES.TOOLS]: 'Tools',
  [MODULES.EXPENSES]: 'Expenses',
  [MODULES.PURCHASE_ORDERS]: 'Purchase Orders',
  [MODULES.VENDORS]: 'Vendors',
  [MODULES.INVENTORY]: 'Inventory',
  [MODULES.RATE_CARDS]: 'Rate Cards',
  [MODULES.CUSTOMER_MESSAGING]: 'Messages',
  // Service Request System
  [MODULES.INCOMING_REQUESTS]: 'Incoming Requests',
  [MODULES.MARKETPLACE]: 'Marketplace',
  // Coming Soon Features
  [MODULES.MOBILE_APP]: 'Mobile Field App',
  [MODULES.GPS_TRACKING]: 'GPS & Routing',
  [MODULES.MARKETING_AUTOMATION]: 'Marketing Automation',
  [MODULES.AI_ESTIMATING]: 'AI Estimating',
  [MODULES.ENHANCED_CUSTOMER_PORTAL]: 'Customer Portal 2.0',
  [MODULES.BUSINESS_INTELLIGENCE]: 'Business Intelligence',
  [MODULES.PAYMENT_PROCESSING]: 'Payment Processing',
  [MODULES.ADVANCED_SCHEDULING]: 'Smart Scheduling'
};

export const MODULE_DESCRIPTIONS = {
  [MODULES.DASHBOARD]: 'View main dashboard and analytics',
  [MODULES.CALENDAR]: 'View schedule calendar and manage time',
  [MODULES.QUOTES]: 'Create and manage quotes',
  [MODULES.JOBS]: 'Complete job management from creation to completion',
  [MODULES.INVOICES]: 'Create and manage invoices',
  [MODULES.CUSTOMERS]: 'Manage customer information',
  [MODULES.CUSTOMER_MESSAGING]: 'Message customers from the company inbox (crew see only their assigned jobs)',
  [MODULES.DOCUMENTS]: 'Access document library',
  [MODULES.SETTINGS]: 'Access company settings',
  [MODULES.EMPLOYEES]: 'Manage team members',
  [MODULES.TIMESHEETS]: 'Track hours logged against jobs',
  [MODULES.LABOR]: 'Employee pay rates, labor costs, and analytics',
  [MODULES.REPORTS]: 'View reports and analytics',
  [MODULES.TOOLS]: 'Access calculators and trade tools',
  [MODULES.EXPENSES]: 'Track business expenses and receipts',
  [MODULES.PURCHASE_ORDERS]: 'Create and manage purchase orders to vendors',
  [MODULES.VENDORS]: 'Manage vendor and supplier relationships',
  [MODULES.INVENTORY]: 'Manage inventory items, stock levels, and movements',
  [MODULES.RATE_CARDS]: 'Manage service pricing and rate cards for quotes',
  // Service Request System
  [MODULES.INCOMING_REQUESTS]: 'View and respond to customer service requests',
  [MODULES.MARKETPLACE]: 'Browse and respond to marketplace service requests',
  // Coming Soon Features
  [MODULES.MOBILE_APP]: 'Native mobile app for field technicians',
  [MODULES.GPS_TRACKING]: 'Real-time GPS tracking and route optimization',
  [MODULES.MARKETING_AUTOMATION]: 'Automated marketing campaigns and follow-ups',
  [MODULES.AI_ESTIMATING]: 'AI-powered estimating from photos',
  [MODULES.ENHANCED_CUSTOMER_PORTAL]: 'Self-service customer portal with payments',
  [MODULES.BUSINESS_INTELLIGENCE]: 'Advanced analytics and reporting dashboards',
  [MODULES.PAYMENT_PROCESSING]: 'Integrated payment processing with lowest rates',
  [MODULES.ADVANCED_SCHEDULING]: 'AI-powered scheduling and dispatch optimization'
};

/**
 * Check if user has access to a specific module
 * @param {Object} user - User object with permissions property
 * @param {string} module - Module to check (from MODULES constant)
 * @returns {boolean} - True if user has access
 */
/**
 * Is this user an owner or admin of their own company?
 *
 * Board #454. Case is NOT reliable on `user.role` and never was. UserContext builds the user
 * object two different ways: some paths set `role: businessUser.role`, the RAW database value,
 * which is lowercase ('owner' on 275 of 275 live owner rows); others set `role: permissionRole`,
 * an UPPERCASE mapped value, and keep the raw one on `db_role`. Which shape you get depends on
 * which code path built it.
 *
 * So `user?.role === 'OWNER'` is a coin flip. It lost on Team > Employees, where it hid the
 * company-wide Time Off tab from the owner entirely — Jerry could file his own time off and
 * receive his technician's request, but had nowhere to see or approve it, while TimeOffPanel
 * sat there already querying every employee's rows.
 *
 * Compare case-insensitively, and check db_role too, so the answer does not depend on which
 * constructor ran. Deliberately a READ-side helper for showing and hiding UI — the permission
 * checks below have the same casing problem and are NOT changed here, because widening them
 * grants access rather than revealing a tab, and that is a decision, not a fix.
 */
export const isOwnerOrAdmin = (user) => {
  const roles = [user?.role, user?.db_role].filter(Boolean).map((r) => String(r).toLowerCase());
  return roles.some((r) => r === 'owner' || r === 'admin' || r === 'app_owner');
};

export const hasModuleAccess = (user, module) => {
  if (!user) {
    return false;
  }
  
  // Owners always have full access
  if (user.role === 'OWNER') {
    return true;
  }
  
  // Non-owners need permissions object
  if (!user.permissions) {
    return false;
  }
  
  // Check specific permission
  return user.permissions[module] === true;
};

/**
 * Check if user can manage permissions (owner or admin with employee access)
 * @param {Object} user - User object
 * @returns {boolean} - True if user can manage permissions
 */
export const canManagePermissions = (user) => {
  if (!user) return false;
  
  // Owners can always manage permissions
  if (user.role === 'OWNER') {
    return true;
  }

  // Admins can manage permissions if they have employee access
  if (user.role === 'ADMIN' && hasModuleAccess(user, MODULES.EMPLOYEES)) {
    return true;
  }
  
  return false;
};

/**
 * Get default permissions for a role
 * @param {string} role - User role (owner/admin/employee)
 * @returns {Object} - Default permissions object
 */
export const getDefaultPermissions = (role) => {
  switch (role) {
    case 'OWNER':
      return {
        [MODULES.DASHBOARD]: true,
        [MODULES.MARKETPLACE]: true,
        [MODULES.CALENDAR]: true,
        [MODULES.QUOTES]: true,
        [MODULES.JOBS]: true,
        [MODULES.INVOICES]: true,
        [MODULES.CUSTOMERS]: true,
        [MODULES.DOCUMENTS]: true,
        [MODULES.SETTINGS]: true,
        [MODULES.EMPLOYEES]: true,
        [MODULES.TIMESHEETS]: true,
        [MODULES.LABOR]: true,
        [MODULES.REPORTS]: true,
        [MODULES.TOOLS]: true,
        [MODULES.EXPENSES]: true,
        [MODULES.PURCHASE_ORDERS]: true,
        [MODULES.VENDORS]: true,
        [MODULES.INVENTORY]: true,
        [MODULES.RATE_CARDS]: true,
        [MODULES.INCOMING_REQUESTS]: true,
        [MODULES.LEADS]: true
      };
    case 'ADMIN':
      return {
        [MODULES.DASHBOARD]: true,
        [MODULES.MARKETPLACE]: true,
        [MODULES.CALENDAR]: true,
        [MODULES.QUOTES]: true,
        [MODULES.JOBS]: true,
        [MODULES.INVOICES]: true,
        [MODULES.CUSTOMERS]: true,
        [MODULES.DOCUMENTS]: true,
        [MODULES.SETTINGS]: false,
        [MODULES.EMPLOYEES]: true,
        [MODULES.TIMESHEETS]: true,
        [MODULES.LABOR]: true,
        [MODULES.REPORTS]: true,
        [MODULES.TOOLS]: true,
        [MODULES.EXPENSES]: true,
        [MODULES.PURCHASE_ORDERS]: true,
        [MODULES.VENDORS]: true,
        [MODULES.INVENTORY]: true,
        [MODULES.RATE_CARDS]: true,
        [MODULES.INCOMING_REQUESTS]: true,
        [MODULES.LEADS]: true
      };
    case 'EMPLOYEE':
    default:
      return {
        [MODULES.DASHBOARD]: true,
        [MODULES.MARKETPLACE]: true, // Employees can browse marketplace
        [MODULES.CALENDAR]: true,
        [MODULES.QUOTES]: false,
        [MODULES.JOBS]: true,
        [MODULES.INVOICES]: false,
        [MODULES.CUSTOMERS]: true, // Changed to true for read-only access
        [MODULES.DOCUMENTS]: true,
        [MODULES.SETTINGS]: false,
        [MODULES.EMPLOYEES]: false,
        [MODULES.TIMESHEETS]: true,
        [MODULES.LABOR]: false,
        [MODULES.REPORTS]: false,
        [MODULES.TOOLS]: true,
        [MODULES.EXPENSES]: false, // Changed to false for employees
        [MODULES.PURCHASE_ORDERS]: false,
        [MODULES.VENDORS]: false,
        [MODULES.INVENTORY]: true, // Employees can view inventory
        [MODULES.INCOMING_REQUESTS]: false, // Only admins can manage incoming requests
        [MODULES.LEADS]: false // Only admins can manage leads
      };
  }
};

/**
 * Get navigation items based on user permissions - New Grouped Structure
 * @param {Object} user - User object with permissions
 * @param {Object} integrations - Integration settings (optional)
 * @returns {Array} - Array of navigation groups user can access
 */
export const getPermittedNavigation = (user, integrations = {}) => {
  const navigation = [];

  // Dashboard — first, and NOT gated. Board #507.
  //
  // This sat behind hasModuleAccess(DASHBOARD), so a role whose stored permissions have that
  // module off got a sidebar starting at Finance with no Dashboard and no Home. Measured on a
  // live Employee session 2026-09-19: the user was looking AT My Dashboard with no control
  // anywhere in the sidebar that returned to it. Navigate away and the primary workspace is
  // gone — reachable only by the command palette or an unlabelled logo.
  //
  // It is not gateable because there is nothing to gate: DashboardRouter gives EVERY
  // authenticated user a dashboard, and a non-admin is sent to MyDashboard, which shows only
  // their own work. Hiding the link never hid any data; it removed the way back.
  //
  // docs/PRODUCT_DECISIONS.md:583 settles this — "The navigation model is the sidebar tree.
  // Dashboard · Marketplace · Work · Sales · Finance · Team · Operations. That is how a user
  // gets anywhere." Dashboard is the first entry of the declared model.
  navigation.push({
    name: MODULE_LABELS[MODULES.DASHBOARD],
    href: '/dashboard',
    icon: HomeIcon,
    module: MODULES.DASHBOARD,
    type: 'single'
  });

  // Marketplace - Second priority as single item
  if (hasModuleAccess(user, MODULES.MARKETPLACE)) {
    navigation.push({
      name: MODULE_LABELS[MODULES.MARKETPLACE],
      href: '/marketplace',
      icon: ShoppingBagIcon,
      module: MODULES.MARKETPLACE,
      type: 'single'
    });
  }

  // Work Section - Jobs, Calendar, Documents
  const workItems = [];
  if (hasModuleAccess(user, MODULES.JOBS)) {
    workItems.push({
      name: 'Jobs',
      href: '/jobs',
      icon: BriefcaseIcon,
      module: MODULES.JOBS
    });
    // Closed Jobs is a view of Jobs, not its own entry (operator ruling 2026-10-19): the
    // Open / Needs invoice / Closed / Cancelled tabs on both pages are the way in, and
    // /jobs/history still lights Jobs through the sidebar's prefix match.
  }
  if (hasModuleAccess(user, MODULES.CALENDAR)) {
    workItems.push({
      name: MODULE_LABELS[MODULES.CALENDAR],
      href: '/calendar',
      icon: CalendarDaysIcon,
      module: MODULES.CALENDAR
    });
  }
  if (hasModuleAccess(user, MODULES.DOCUMENTS)) {
    workItems.push({
      name: MODULE_LABELS[MODULES.DOCUMENTS],
      href: '/documents',
      icon: FolderIcon,
      module: MODULES.DOCUMENTS
    });
  }

  if (workItems.length > 0) {
    navigation.push({
      name: 'Work',
      icon: BriefcaseIcon,
      type: 'group',
      items: workItems
    });
  }

  // Sales Section - Customers, Quotes, Invoices (for Owner/Admin)
  const salesItems = [];

  if (hasModuleAccess(user, MODULES.CUSTOMERS)) {
    salesItems.push({
      name: MODULE_LABELS[MODULES.CUSTOMERS],
      href: '/customers',
      icon: UserGroupIcon,
      module: MODULES.CUSTOMERS
    });
  }
  if (hasModuleAccess(user, MODULES.QUOTES)) {
    salesItems.push({
      name: MODULE_LABELS[MODULES.QUOTES],
      href: '/quotes',
      icon: DocumentTextIcon,
      module: MODULES.QUOTES
    });
  }
  if (hasModuleAccess(user, MODULES.QUOTES)) {
    salesItems.push({
      name: 'Job Templates',
      href: '/templates',
      icon: ClipboardDocumentListIcon,
      module: MODULES.QUOTES
    });
  }
  if (hasModuleAccess(user, MODULES.QUOTES)) {
    salesItems.push({
      name: 'Price Book',
      href: '/price-book',
      icon: BookOpenIcon,
      module: MODULES.QUOTES
    });
  }
  if (hasModuleAccess(user, MODULES.INVOICES)) {
    salesItems.push({
      name: MODULE_LABELS[MODULES.INVOICES],
      href: '/invoices',
      icon: CurrencyDollarIcon,
      module: MODULES.INVOICES
    });
  }

  // Disputes & Reviews — available to anyone who can see invoices
  if (hasModuleAccess(user, MODULES.INVOICES)) {
    salesItems.push({
      name: 'Disputes & Reviews',
      href: '/disputes',
      icon: ExclamationTriangleIcon,
      module: MODULES.INVOICES
    });
  }

  // Incoming Requests (lead inbox) nav entry retired — consolidated into the
  // Marketplace Browse surface. Reach leads via Marketplace → Providing → Browse.

  if (salesItems.length > 0) {
    navigation.push({
      name: 'Sales',
      icon: CurrencyDollarIcon,
      type: 'group',
      items: salesItems
    });
  }

  // For employees - show Customers as read-only single item if they have access
  if (user?.role === 'EMPLOYEE' && hasModuleAccess(user, MODULES.CUSTOMERS)) {
    navigation.push({
      name: MODULE_LABELS[MODULES.CUSTOMERS],
      href: '/customers',
      icon: UserGroupIcon,
      module: MODULES.CUSTOMERS,
      type: 'single',
      badge: 'Read-only'
    });
  }

  // Finance Section - Expenses, Reports, Payroll (Owner/Admin only)
  const financeItems = [];
  if (hasModuleAccess(user, MODULES.EXPENSES)) {
    financeItems.push({
      name: MODULE_LABELS[MODULES.EXPENSES],
      href: '/expenses',
      icon: BanknotesIcon,
      module: MODULES.EXPENSES
    });
  }
  if (hasModuleAccess(user, MODULES.PURCHASE_ORDERS)) {
    financeItems.push({
      name: MODULE_LABELS[MODULES.PURCHASE_ORDERS],
      href: '/purchase-orders',
      icon: DocumentCheckIcon,
      module: MODULES.PURCHASE_ORDERS
    });
  }
  if (hasModuleAccess(user, MODULES.VENDORS)) {
    financeItems.push({
      name: MODULE_LABELS[MODULES.VENDORS],
      href: '/vendors',
      icon: BuildingOfficeIcon,
      module: MODULES.VENDORS
    });
  }
  if (hasModuleAccess(user, MODULES.REPORTS)) {
    financeItems.push({
      name: MODULE_LABELS[MODULES.REPORTS],
      href: '/reports',
      icon: ChartBarIcon,
      module: MODULES.REPORTS
    });
  }
  if (hasModuleAccess(user, MODULES.LABOR)) {
    financeItems.push({
      name: MODULE_LABELS[MODULES.LABOR],
      href: '/labor',
      icon: BanknotesIcon,
      module: MODULES.LABOR
    });
  }

  // Add integration items to Finance section
  if (integrations.enableQuickbooks) {
    financeItems.push({
      name: 'QuickBooks',
      href: '/expenses',
      icon: ChartBarIcon,
      badge: 'Integration'
    });
  }

  if (financeItems.length > 0) {
    navigation.push({
      name: 'Finance',
      icon: ChartBarIcon,
      type: 'group',
      items: financeItems
    });
  }

  // Team Section - Employees, Timesheets (Owner/Admin only)
  const teamItems = [];
  if (hasModuleAccess(user, MODULES.EMPLOYEES)) {
    teamItems.push({
      name: MODULE_LABELS[MODULES.EMPLOYEES],
      href: '/employees',
      icon: UsersIcon,
      module: MODULES.EMPLOYEES
    });
  }
  if (hasModuleAccess(user, MODULES.TIMESHEETS) && user?.role !== ROLES.EMPLOYEE) {
    teamItems.push({
      name: MODULE_LABELS[MODULES.TIMESHEETS],
      href: '/timesheets',
      icon: ClockIcon,
      module: MODULES.TIMESHEETS
    });
  }

  // Team Chat is INTERNAL staff communication and never touches a customer, so it
  // belongs with the people it is about. It sat under Operations next to Messages,
  // which is CUSTOMER communication — the two look alike and are opposites, and
  // grouping them by "it's a chat window" put an internal tool one row from a
  // customer-facing one. Jobber and HouseCall Pro both file team messaging under
  // the team/staff area for the same reason.
  //
  // No module gate, deliberately: MODULES has no TEAM_CHAT key, so hasModuleAccess
  // would return false for everyone and hide it. That is also why the old entry
  // carried the bare string 'TEAM_CHAT' rather than a MODULES reference — inert,
  // because the sidebar does not filter on item.module.
  teamItems.push({
    name: 'Team Chat',
    href: '/team-chat',
    icon: LockClosedIcon,
    badge: 'Internal'
  });

  if (teamItems.length > 0) {
    navigation.push({
      name: 'Team',
      icon: UsersIcon,
      type: 'group',
      items: teamItems
    });
  }

  // For employees - split into My Time and My Time Off
  if (user?.role === 'EMPLOYEE' && hasModuleAccess(user, MODULES.TIMESHEETS)) {
    navigation.push({
      name: 'My Time',
      href: '/my/time',
      icon: ClockIcon,
      module: MODULES.TIMESHEETS,
      type: 'single'
    });
    navigation.push({
      name: 'My Time Off',
      href: '/my/time-off',
      icon: CalendarDaysIcon,
      module: MODULES.TIMESHEETS,
      type: 'single'
    });
  }

  // Admin approvals are accessible from Admin Dashboard quick actions to reduce sidebar clutter

  // Operations Section - Tools, Inventory, Messages, Integrations
  const operationsItems = [];
  if (hasModuleAccess(user, MODULES.TOOLS)) {
    operationsItems.push({
      name: MODULE_LABELS[MODULES.TOOLS],
      href: '/tools',
      icon: WrenchScrewdriverIcon,
      module: MODULES.TOOLS
    });
  }

  if (hasModuleAccess(user, MODULES.INVENTORY)) {
    operationsItems.push({
      name: MODULE_LABELS[MODULES.INVENTORY],
      href: '/operations/inventory',
      icon: ArchiveBoxIcon,
      module: MODULES.INVENTORY
    });
  }

  // The SMS / Cloud Storage / CRM links were gated on enable_sms / enable_cloud_storage /
  // enable_crm, columns no table has (verified live 2026-10-30), so they could never show.
  // Removed per operator ruling 11, 2026-10-30 (hide, don't build) — docs/PRODUCT_DECISIONS.md.
  // The Automation link (/automation, hardcoded sample data, gated on the equally missing
  // enable_zapier) was unlinked by the same ruling.

  // Incoming Requests moved to Sales section as "Leads"

  // Messages - customer communication.
  //
  // Board #1245/#1246. This was pushed unconditionally, and its `module: 'MESSAGES'` was not even
  // a real key in MODULES — so it matched nothing, gated nothing, and every technician got a
  // sidebar link into the customer inbox. Now that /messages is behind CUSTOMER_MESSAGING, an
  // ungated link would be the dead-end shape board #502 is about: a control that leads somewhere
  // the user is refused. Same hasModuleAccess test as every other item here, with the real key.
  if (hasModuleAccess(user, MODULES.CUSTOMER_MESSAGING)) {
    operationsItems.push({
      name: 'Messages',
      href: '/messages',
      icon: ChatBubbleLeftRightIcon,
      module: MODULES.CUSTOMER_MESSAGING
    });
  }

  // Team Chat moved to the Team group (2026-08-31) — internal staff comms belong with
  // the team, not beside Messages, which is customer comms.

  if (operationsItems.length > 0) {
    navigation.push({
      name: 'Operations',
      icon: WrenchScrewdriverIcon,
      type: 'group',
      items: operationsItems
    });
  }

  // Coming Soon nav section removed — placeholder pages deleted

  // Account Section - Settings, Profile, Sign Out (temporary placement)
  const accountItems = [];

  if (hasModuleAccess(user, MODULES.SETTINGS)) {
    accountItems.push({
      name: MODULE_LABELS[MODULES.SETTINGS],
      href: '/settings',
      icon: CogIcon,
      module: MODULES.SETTINGS
    });
  }

  // 'My Profile' link is surfaced in My Dashboard and user menu; keep sidebar Account minimal

  accountItems.push({
    name: 'Sign Out',
    href: '#',
    icon: ArrowRightOnRectangleIcon,
    isLogout: true
  });

  if (accountItems.length > 0) {
    navigation.push({
      name: 'Account',
      icon: UserIcon,
      type: 'group',
      items: accountItems
    });
  }

  // All navigation items are now properly grouped above - no loose items needed

  return navigation;
};

/**
 * Get all available modules for permission management
 * @returns {Array} - Array of module objects
 */
export const getAllModules = () => {
  return Object.values(MODULES).map(module => ({
    key: module,
    label: MODULE_LABELS[module],
    description: MODULE_DESCRIPTIONS[module]
  }));
};

/**
 * Validate permissions object
 * @param {Object} permissions - Permissions object to validate
 * @returns {Object} - Validated permissions object
 */
export const validatePermissions = (permissions) => {
  const validated = {};
  
  Object.values(MODULES).forEach(module => {
    validated[module] = permissions[module] === true;
  });
  
  return validated;
};

/**
 * Check if user is in same company as target user
 * @param {Object} currentUser - Current user
 * @param {Object} targetUser - Target user
 * @returns {boolean} - True if same company
 */
export const isSameCompany = (currentUser, targetUser) => {
  return currentUser?.company_id === targetUser?.company_id;
};

/**
 * Check if user can edit target user's permissions
 * @param {Object} currentUser - Current user
 * @param {Object} targetUser - Target user
 * @returns {boolean} - True if can edit
 */
export const canEditUserPermissions = (currentUser, targetUser) => {
  // Must be able to manage permissions
  if (!canManagePermissions(currentUser)) {
    return false;
  }
  
  // Must be same company
  if (!isSameCompany(currentUser, targetUser)) {
    return false;
  }
  
  // Cannot edit owner permissions
  if (targetUser?.role === 'owner') {
    return false;
  }
  
  // Cannot edit your own permissions unless you're owner
  if (currentUser?.id === targetUser?.id && currentUser?.role !== 'owner') {
    return false;
  }
  
  return true;
};
